import { useEffect, useRef, useState } from 'react';
import PhoneSensorPlayer from '../sensors/PhoneSensorPlayer';
import DjiStreamPlayer from '../DjiStreamPlayer';
import HlsPlayer from './HlsPlayer';
import ProcessedStreamPlayer from './ProcessedStreamPlayer';
import VideoStandby from './VideoStandby';
import { isFeedDead } from './feedWatchdog';
import { useFeedWebrtc, type FeedWebrtcCreds } from './useFeedWebrtc';
import { HANDBACK_TIMEOUT_MS, stableCreds } from './feedWebrtc';
import { needsServerProcessing, prefersServerPicture, prefersPrimaryWebrtc } from './serverProcessing';
import { channelFor } from './aiChannels';

/** How often to check that the MJPEG picture is still there. */
const FEED_WATCHDOG_INTERVAL_MS = 2000;

export interface StreamSource {
  id: string;
  name: string;
  url: string;
  type: 'dummy' | 'rtmp' | 'rtsp' | 'dji' | 'phone' | 'hls' | 'demo' | 'picam';
}

export interface DjiCredentials {
  appId: string;
  channel: string;
  token: string;
  uid: number;
}

export interface PhoneCredentials {
  livekitUrl: string;
  token: string;
  sensorId: number;
}

export interface ProcessedStreamCredentials {
  livekitUrl: string;
  token: string;
  /**
   * What the primary room is actually publishing, as the server names it.
   *
   * That room carries the PROCESSING feed and nothing else, so a panel showing a
   * different camera must not subscribe — it would put the wrong picture on
   * screen under the right camera's name. Same shape `ai_channel_routes._ser`
   * echoes, so it is matched with `channelFor()` and the client still never
   * derives a stream key. Absent on an older backend, which reads as
   * "cannot verify" and keeps the panel on MJPEG unless an AI layer is on.
   */
  feed?: { source_type?: string | null; source_url?: string | null;
           device_sn?: string | null } | null;
}

export type AuthHeadersFn = (contentType?: boolean) => HeadersInit;

export interface HlsCredentials {
  hlsUrl: string;
  deviceSn: string;
  displayName?: string;
}

export interface VideoRendererProps {
  activeSourceId: string;
  activeStreamSource?: StreamSource;
  phoneSensorCredentialsMap: Record<string, PhoneCredentials>;
  hlsCredentialsMap: Record<string, HlsCredentials>;
  djiStreamCredentials: DjiCredentials | null;
  processedStreamCreds?: ProcessedStreamCredentials | null;
  feedToken: string | null;
  videoKey: number;
  detectionEnabled: boolean;
  roadSegEnabled?: boolean;
  geoMaskEnabled?: boolean;
  apiUrl: string;
  authHeaders: AuthHeadersFn;
  djiStreamError: string | null;
  onDjiError: (msg: string) => void;
  onDjiDisconnected: () => void;
  onPhoneDisconnected: (sourceId: string) => void;
  onRecordingSaved: () => void;
  onProcessedStreamError?: () => void;
  /** The MJPEG <img> failed to load — expired feed token, backend restart or a
   *  network blip. Without this the panel just stays black: an <img> that 401s
   *  renders nothing, and nothing here retries. */
  onFeedError?: () => void;
  /** Per-viewer MJPEG cadence and JPEG quality. Omitted (the full-size video
   *  panel) means the server's defaults — 30fps at the compositor's own
   *  quality, byte-for-byte what it always served. A Matrix tile asks for less
   *  of both: it renders at ~320x180, and MJPEG charges full price per frame
   *  whether the picture moved or not. Neither costs a second compositor —
   *  `fps` only paces the yield loop, and one encode per quality is shared by
   *  every viewer of that feed. See MATRIX_FEED_FPS in backend/main.py. */
  feedFps?: number;
  feedQuality?: number;
  /** Try WebRTC for this tile, falling back to MJPEG whenever it is unavailable.
   *  Set by VideoGrid: a grid is where MJPEG's per-tile bandwidth actually bites
   *  (measured ~3.5 Mbit/s each even after the fps/quality cut, against ~0.6 over
   *  WebRTC), while a single full-size feed is better off on MJPEG's lower
   *  latency. Off by default so nothing changes for callers that do not opt in. */
  preferWebrtc?: boolean;
  /** This deployment set DJI_LOW_LATENCY: serve an aircraft from the reader
   *  already pulling its RTMP instead of leaving the tile on HLS, which here is
   *  ~4-8 s behind and cannot be tuned lower (nginx-rtmp cuts on a keyframe and
   *  DJI exposes no GOP knob). From `GET /api/drone/overlays`, never a build-time
   *  constant — the client cannot derive it from an `.m3u8`. Undefined until the
   *  first poll lands, which reads as off. See prefersServerPicture(). */
  djiLowLatency?: boolean;
}

export default function VideoRenderer({
  activeSourceId,
  activeStreamSource,
  phoneSensorCredentialsMap,
  hlsCredentialsMap,
  djiStreamCredentials,
  processedStreamCreds,
  feedToken,
  videoKey,
  detectionEnabled,
  roadSegEnabled,
  geoMaskEnabled,
  apiUrl,
  authHeaders,
  djiStreamError,
  onDjiError,
  onDjiDisconnected,
  onPhoneDisconnected,
  onRecordingSaved,
  onProcessedStreamError,
  onFeedError,
  feedFps,
  feedQuality,
  preferWebrtc,
  djiLowLatency,
}: VideoRendererProps) {
  const phoneCreds = activeSourceId.startsWith('phone-') ? phoneSensorCredentialsMap[activeSourceId] : null;
  const hlsCreds = hlsCredentialsMap[activeSourceId] || null;
  const aiLayerActive = needsServerProcessing(roadSegEnabled, geoMaskEnabled, detectionEnabled);
  // An HLS tile abandons its own player either because a model's output has to be
  // in the picture (the historical rule) or because this deployment would rather
  // not watch an aircraft seconds late. Both answers come from one predicate so
  // the three sites below cannot disagree about which picture this tile shows.
  const useServerProcessing = prefersServerPicture({
    aiLayerActive, hasDeviceSn: !!hlsCreds?.deviceSn, lowLatency: djiLowLatency,
  }) && hlsCreds;

  const feedSource = useServerProcessing ? 'rtmp' : (activeStreamSource?.type || 'dummy');
  const feedUrl = activeStreamSource?.url || '';
  let mjpegSrc = `${apiUrl}/api/drone/feed?token=${feedToken}&t=${videoKey}&source=${feedSource}`;
  if (feedSource === 'rtmp' && feedUrl && !useServerProcessing) {
    mjpegSrc += `&url=${encodeURIComponent(feedUrl)}`;
  }
  // A swapped DJI tile names its aircraft. Without this the request is just
  // "the rtmp feed", which the backend resolves to whatever the PROCESSING
  // source happens to be — right only while the drone is also the main panel's
  // source, and silently the wrong camera in a grid where it is not.
  if (useServerProcessing && hlsCreds?.deviceSn) {
    mjpegSrc += `&device_sn=${encodeURIComponent(hlsCreds.deviceSn)}`;
  }
  if (feedFps) mjpegSrc += `&fps=${feedFps}`;
  if (feedQuality) mjpegSrc += `&quality=${feedQuality}`;
  // The Pi-guided aircraft: a byte relay of the Pi's own MJPEG (4:3, overlay burned
  // in by the Pi), never the 640x360 reader — see docs/PICAM_GUIDANCE.md.
  const isPicam = activeStreamSource?.type === 'picam';
  if (isPicam) mjpegSrc = `${apiUrl}/api/picam/stream?token=${feedToken}&t=${videoKey}`;

  // The panel may take the primary room's picture instead of opening an MJPEG
  // connection. `matchesProcessingFeed` is what stops it showing camera Y while
  // labelled camera X — see prefersPrimaryWebrtc() for why that is checked rather
  // than assumed. Matched with the channel matcher so there is one rule for
  // "is this server-side feed the one this tile shows", not two.
  const processingFeed = processedStreamCreds?.feed;
  const matchesProcessingFeed = !!processingFeed && !!activeStreamSource && !!channelFor(
    { id: activeSourceId, name: '', type: activeStreamSource.type,
      url: activeStreamSource.url, deviceSn: hlsCreds?.deviceSn },
    [{ stream_key: '', name: '', model_slug: null, road_seg: false,
       source_type: processingFeed.source_type ?? undefined,
       source_url: processingFeed.source_url ?? undefined,
       device_sn: processingFeed.device_sn ?? undefined }]);
  const useWebRTC = !isPicam && !!processedStreamCreds && prefersPrimaryWebrtc({
    hasCreds: true,
    // Deliberately the AI-layer answer alone, NOT useServerProcessing: the
    // primary room carries the processing feed, and `matchesProcessingFeed`
    // below is what decides whether that is this tile's aircraft.
    aiLayerActive,
    matchesProcessingFeed,
  }) ? processedStreamCreds : null;

  // Per-feed WebRTC for grid tiles. null whenever it is unavailable — no LiveKit,
  // a browser-decoded source, the publisher budget is full, or the request failed
  // — and null means MJPEG, never a blank tile. Suppressed while the primary
  // publisher already serves this panel over WebRTC, so one tile cannot hold two
  // LiveKit rooms open for the same picture.
  // An HLS tile is suppressed while it is showing its own player — there is no
  // composited picture to subscribe to. Once it has swapped to the server's
  // picture it is an ordinary server-read feed, addressed by aircraft serial.
  const feedWebrtc = useFeedWebrtc(
    apiUrl, authHeaders,
    !!preferWebrtc && !useWebRTC && !phoneCreds && !isPicam && (!hlsCreds || !!useServerProcessing),
    activeStreamSource?.type, activeStreamSource?.url,
    useServerProcessing ? hlsCreds?.deviceSn : undefined,
  );

  // Watchdog over the MJPEG <img>. It has to poll: a multipart stream that dies
  // mid-flight leaves the element mounted and `complete`, with the picture gone
  // and no event raised (see feedWatchdog.ts). Restarted whenever the <img> is
  // replaced, which mjpegSrc changing already tracks.
  // Whether the per-feed WebRTC player has an actual picture. Until it does, the
  // MJPEG <img> stays on top — see the render below.
  const [webrtcLive, setWebrtcLive] = useState(false);
  useEffect(() => { setWebrtcLive(false); }, [feedWebrtc?.room]);

  // What the player actually gets. NOT `feedWebrtc` directly: the lease renews
  // every 30 s with a freshly minted JWT, and the player is keyed on its
  // credentials, so passing each one through disconnected a working room and
  // re-did ICE — ~1 s of black per tile per renewal, which in a grid reads as
  // random tiles blinking. `stableCreds` holds a live room steady and lets a
  // renewal through only when it is the retry that revives a dead one — the rule
  // and the measurements are in feedWebrtc.ts. Liveness is read from a ref so a
  // disconnect does not by itself swap credentials — the next renewal does that,
  // exactly as before.
  const webrtcLiveRef = useRef(false);
  webrtcLiveRef.current = webrtcLive;
  const [heldCreds, setHeldCreds] = useState<FeedWebrtcCreds | null>(null);
  useEffect(() => {
    setHeldCreds(prev => stableCreds(prev, feedWebrtc, webrtcLiveRef.current));
  }, [feedWebrtc]);

  const imgRef = useRef<HTMLImageElement | null>(null);

  // The last creds we had, kept so the player can stay mounted for a moment AFTER
  // they go away. Losing WebRTC — the Matrix closing, the publisher stopping, ICE
  // failing — means MJPEG has to reconnect, and an <img> that has just mounted has
  // no picture yet. Without this the handback showed black and the server's
  // CONNECTING card, which is the same flicker the forward direction already
  // fixed, just on the way out. Updated in an effect, so the render where the
  // credentials first become null still sees the previous value.
  //
  // It retains what the PLAYER held, not the last lease: a different token object
  // at handback time would change the player's deps and reconnect it, losing the
  // very frame this exists to keep on screen.
  const retainedCreds = useRef<FeedWebrtcCreds | null>(null);
  useEffect(() => { if (heldCreds) retainedCreds.current = heldCreds; }, [heldCreds]);

  const handingBack = !heldCreds && webrtcLive && !!retainedCreds.current;
  const playerCreds = heldCreds ?? (handingBack ? retainedCreds.current : null);

  // End the handback once MJPEG actually has a picture. Polled rather than trusting
  // the <img> load event, which a multipart stream fires inconsistently across
  // browsers — the same reason feedWatchdog.ts polls. The timeout is the backstop:
  // if MJPEG never comes back we must still stop showing a frozen WebRTC frame,
  // because a frozen picture reads as a live camera and is the worse failure.
  useEffect(() => {
    if (!handingBack) return;
    const done = () => setWebrtcLive(false);
    const poll = setInterval(() => {
      if ((imgRef.current?.naturalWidth ?? 0) > 0) done();
    }, 250);
    const giveUp = setTimeout(done, HANDBACK_TIMEOUT_MS);
    return () => { clearInterval(poll); clearTimeout(giveUp); };
  }, [handingBack]);
  const everHadPicture = useRef(false);
  useEffect(() => {
    if (!onFeedError) return;
    everHadPicture.current = false;
    const mountedAt = Date.now();
    const id = setInterval(() => {
      const img = imgRef.current;
      if (!img) return;
      if (img.naturalWidth > 0) {
        everHadPicture.current = true;
        return;
      }
      if (isFeedDead({
        naturalWidth: img.naturalWidth,
        everHadPicture: everHadPicture.current,
        msSinceMount: Date.now() - mountedAt,
      })) {
        onFeedError();
      }
    }, FEED_WATCHDOG_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mjpegSrc, onFeedError]);

  return (
    <>
      {phoneCreds ? (
        <PhoneSensorPlayer
          livekitUrl={phoneCreds.livekitUrl}
          token={phoneCreds.token}
          sensorId={phoneCreds.sensorId}
          detectionEnabled={detectionEnabled}
          apiUrl={apiUrl}
          authHeaders={authHeaders}
          onError={onDjiError}
          onDisconnected={() => onPhoneDisconnected(activeSourceId)}
          onRecordingSaved={onRecordingSaved}
        />
      ) : hlsCreds && !useServerProcessing ? (
        <HlsPlayer
          hlsUrl={hlsCreds.hlsUrl}
          deviceSn={hlsCreds.deviceSn}
          displayName={hlsCreds.displayName}
          detectionEnabled={detectionEnabled}
          apiUrl={apiUrl}
          authHeaders={authHeaders}
          onError={onDjiError}
          onDisconnected={onDjiDisconnected}
        />
      ) : useWebRTC ? (
        <ProcessedStreamPlayer
          /* Read off `useWebRTC`, not `processedStreamCreds`: it IS the
             credentials when the rule says yes, and null when it does not, so
             the two can never disagree about which the player gets. */
          livekitUrl={useWebRTC.livekitUrl}
          token={useWebRTC.token}
          onError={(msg) => {
            console.warn('[ProcessedStream] WebRTC error, falling back to MJPEG:', msg);
            onProcessedStreamError?.();
          }}
          onDisconnected={() => {
            console.warn('[ProcessedStream] WebRTC disconnected, falling back to MJPEG');
            onProcessedStreamError?.();
          }}
        />
      ) : activeSourceId === 'dji' && djiStreamCredentials ? (
        <DjiStreamPlayer
          credentials={djiStreamCredentials}
          detectionEnabled={detectionEnabled}
          apiUrl={apiUrl}
          onError={onDjiError}
          onDisconnected={onDjiDisconnected}
        />
      ) : !activeSourceId ? (
        /* Nothing selected — client-side standby, no MJPEG connection opened */
        <VideoStandby />
      ) : feedToken ? (
        /* MJPEG, plus the per-feed WebRTC player layered over it.
           BOTH run during the handover, deliberately. Switching the moment the
           token arrives showed a black cell and "Connecting via WebRTC..." for a
           second or two on every tile — WebRTC needs an ICE handshake and a
           keyframe before it has anything to draw. Keeping MJPEG on top until
           `onConnected` fires makes the handover picture-to-picture. Both carry a
           stable `key` so the <img> is not remounted when the player appears
           beside it; a remount would open a second MJPEG connection and show the
           server's CONNECTING card, which is the flicker this exists to remove. */
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {playerCreds && (
          /* zIndex flips with the direction. Coming IN, MJPEG is on top and the
             player loads behind it. Going OUT, the player keeps the last WebRTC
             frame on top while MJPEG reconnects underneath. Either way the
             operator never sees black. */
          <div key="webrtc" style={{ position: 'absolute', inset: 0, zIndex: handingBack ? 2 : 1 }}>
            <ProcessedStreamPlayer
              livekitUrl={playerCreds.livekitUrl}
              token={playerCreds.token}
              onConnected={() => setWebrtcLive(true)}
              onDisconnected={() => setWebrtcLive(false)}
              onError={(msg) => {
                // Deliberately NOT onProcessedStreamError: that clears the
                // PRIMARY publisher's credentials, which this tile does not own.
                // Dropping back to MJPEG is the whole recovery.
                setWebrtcLive(false);
                console.warn(`[feed-webrtc] ${playerCreds.room}: ${msg}`);
              }}
            />
          </div>
        )}
        {!(heldCreds && webrtcLive) && (
        <img
          ref={imgRef}
          src={mjpegSrc}
          /* Deliberately empty. A live MJPEG stream that has not delivered a frame yet, or
             whose request failed, makes the browser paint the alt text over the black panel —
             which reads as stray words on the video, not as a status. The panel already says
             what is happening properly: the server's CONNECTING and NO SIGNAL cards, and
             VideoStandby when nothing is selected. Empty (not absent) so the image stays
             decorative rather than being announced by its URL. */
          alt=""
          className="video-stream"
          key={`${videoKey}-${feedToken}`}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                   zIndex: handingBack ? 1 : 2 }}
          /* Covers the other half: a request that fails outright (expired feed
             token) does raise `error`, unlike a stream that dies mid-flight. */
          onError={onFeedError}
        />
        )}
        </div>
      ) : (
        <div className="video-overlay" style={{position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
          <span>Authenticating feed...</span>
        </div>
      )}
      {djiStreamError && (
        <div className="video-overlay" style={{background: 'rgba(255,0,0,0.7)', top: 'auto', bottom: '0'}}>
          <span>{djiStreamError}</span>
        </div>
      )}
      {!activeSourceId.startsWith('phone-') && (!hlsCreds || useServerProcessing) &&
        (activeStreamSource || hlsCreds || activeSourceId === 'dji') && (
        /* No badge when nothing is selected — the NO SIGNAL frame speaks for itself */
        <div className="video-overlay">
          <span className="drone-id">{activeSourceId === 'dji' ? 'DJI-M30T' : hlsCreds?.displayName || activeStreamSource?.name || 'DRONE-001'}</span>
        </div>
      )}
    </>
  );
}
